import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaletteIcon from "@mui/icons-material/Palette";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import BadgeIcon from "@mui/icons-material/Badge";
import TitleIcon from "@mui/icons-material/Title";
import NotesIcon from "@mui/icons-material/Notes";
import CropIcon from "@mui/icons-material/Crop";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Kleines Symbol pro Übergangs-Typ, damit man Welle/Schräge/… auf einen Blick
// erkennt (statt nur am Text). Zeichnet die tatsächliche Kantenform als SVG,
// monochrom in der aktuellen Textfarbe (currentColor -> passt sich dem
// aktiven/inaktiven Button-Zustand an).
function DividerTypeIcon({ type }) {
  const common = {
    width: 20,
    height: 12,
    viewBox: "0 0 20 12",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    style: { display: "block", flex: "0 0 auto" },
  };
  switch (type) {
    case "none": // gerade Kante
      return (
        <svg {...common}>
          <path d="M2 6h16" />
        </svg>
      );
    case "angle-right": // Schräge, die nach rechts oben ansteigt
      return (
        <svg {...common}>
          <path d="M2 9L18 3" />
        </svg>
      );
    case "angle-left": // Schräge, die nach rechts unten abfällt
      return (
        <svg {...common}>
          <path d="M2 3L18 9" />
        </svg>
      );
    case "wave": // Welle
      return (
        <svg {...common}>
          <path d="M2 6 Q6 1 10 6 T18 6" />
        </svg>
      );
    case "zigzag": // Zickzack
      return (
        <svg {...common}>
          <path d="M2 8 L5 4 L8 8 L11 4 L14 8 L17 4 L18 6" />
        </svg>
      );
    case "scallop": // Bögen
      return (
        <svg {...common}>
          <path d="M2 8 A2 2 0 0 1 6 8 A2 2 0 0 1 10 8 A2 2 0 0 1 14 8 A2 2 0 0 1 18 8" />
        </svg>
      );
    case "gradient": // Verlauf: drei nach unten ausblendende Linien
      return (
        <svg {...common}>
          <path d="M2 3h16" opacity="0.95" />
          <path d="M2 6.5h16" opacity="0.55" />
          <path d="M2 10h16" opacity="0.25" />
        </svg>
      );
    default:
      return null;
  }
}

// Icon für die Ecken-Rundung eines Elements: zeichnet ein Quadrat mit dem
// jeweiligen Eckenradius, monochrom in der aktuellen Textfarbe.
function RoundingIcon({ id }) {
  const rx = id === "high" ? 6 : id === "low" ? 2.5 : 0;
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      aria-hidden
      style={{ display: "block", flex: "0 0 auto" }}
    >
      <rect x="2.5" y="2.5" width="11" height="11" rx={rx} ry={rx} />
    </svg>
  );
}

export default function DesignEditor({
  open,
  onClose,
  previewPages,
  previewIndex,
  setPreviewIndex,
  previewPanePct,
  setPreviewPanePct,
  previewError,
  previewLoading,
  onDownloadPdf,
  onRefreshPreview,
  pdfLoading,
  onFillLorem,
  onFillMinimum,
  onFillMaximum,
  onPremiumClick,
  isPro,
  designId,
  setDesignId,
  designTemplates,
  colorTemplates,
  isCustomActive,
  setUseCustomColors,
  customColors,
  setCustomColors,
  templateId,
  setTemplateId,
  fontId,
  setFontId,
  fontOptions,
  bodyFontOptions,
  nameFontId,
  setNameFontId,
  nameFontSize,
  setNameFontSize,
  nameColorKey,
  setNameColorKey,
  nameOutlineEnabled,
  setNameOutlineEnabled,
  nameOutlineColorKey,
  setNameOutlineColorKey,
  dividerStyle,
  setDividerStyle,
  dividerConfig,
  setDividerConfig,
  dividerTypeOptions = [],
  dividerSpacingStep = 6,
  footerEnabled,
  setFooterEnabled,
  sectionLabels,
  sectionModes,
  setSectionModes,
  sectionColorModes,
  sectionOrder,
  setSectionOrder,
  sectionPadding,
  setSectionPadding,
  sectionGeometry,
  titleColorKey,
  setTitleColorKey,
  bodyColorKey,
  setBodyColorKey,
  previewPalette,
  accentColor,
  sectionVisibility,
  setSectionVisibility,
  titleCtaLinkEnabled,
  setTitleCtaLinkEnabled,
  titleCtaQrEnabled,
  setTitleCtaQrEnabled,
  titleFontId,
  setTitleFontId,
  titleFontSize,
  setTitleFontSize,
  bodyFontSize,
  setBodyFontSize,
  bodySizeOptions: bodySizeOptionsProp,
  bodySizeDisabledValues = [],
  lineSpacingDisabledIds = [],
  heroOffset,
  setHeroOffset,
  portraitOffset,
  setPortraitOffset,
  onOpenHeroCrop,
  onOpenPortraitCrop,
  hasHeroPhoto,
  hasPortraitPhoto,
  lineSpacing,
  setLineSpacing,
  lineSpacingOptions = [],
  bioImageScale,
  setBioImageScale,
  elementRoundness,
  setElementRoundness,
}) {
  const [expanded, setExpanded] = useState("colors");
  const [contentMenuAnchor, setContentMenuAnchor] = useState(null);
  const [showEditHint, setShowEditHint] = useState(() => {
    try {
      return !localStorage.getItem("mk_editor_hint_seen");
    } catch {
      return true;
    }
  });
  const dismissEditHint = () => {
    setShowEditHint(false);
    try {
      localStorage.setItem("mk_editor_hint_seen", "1");
    } catch {
      /* ignore */
    }
  };

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    const next = isExpanded ? panel : false;
    setExpanded(next);
    if (next === "front") {
      setPreviewIndex(0);
    }
    if (next === "nameStyle") {
      setPreviewIndex(0);
    }
    if (next === "sections" && previewIndex === 0 && previewPages.length > 1) {
      setPreviewIndex(1);
    }
    if (next === "dividers" && previewIndex === 0 && previewPages.length > 1) {
      setPreviewIndex(1);
    }
    if (next === "titleStyle" && previewIndex === 0 && previewPages.length > 1) {
      setPreviewIndex(1);
    }
  };

  const palettePreview = useMemo(() => previewPalette, [previewPalette]);
  const totalPages = previewPages.length;
  const currentPage = totalPages ? previewIndex + 1 : 0;
  const nameSizeOptions = [
    { id: "small", label: "Klein", value: 50 },
    { id: "medium", label: "Mittel", value: 70 },
    { id: "large", label: "Groß", value: 94 },
  ];
  const titleSizeOptions = [
    { id: "small", label: "Klein", value: 26 },
    { id: "medium", label: "Mittel", value: 32 },
    { id: "large", label: "Groß", value: 40 },
  ];
  const bodySizeOptions = bodySizeOptionsProp || [
    { id: "small", label: "Klein", value: 12 },
    { id: "medium", label: "Mittel", value: 13.5 },
    { id: "large", label: "Groß", value: 15 },
  ];
  const elementRoundnessOptions = [
    { id: "off", label: "Aus" },
    { id: "low", label: "Wenig" },
    { id: "high", label: "Stark" },
  ];
  const nameColorOptions = [
    { id: "c1", label: "Farbe 1", key: "c1" },
    { id: "c2", label: "Farbe 2", key: "c2" },
    { id: "c3", label: "Farbe 3", key: "c3" },
  ];
  const resolvePaletteColor = (key) => (palettePreview && palettePreview[key]) || "#111827";

  // --- Farb-Helfer: Kontrastprüfung & automatischer Vorschlag für eigene Farben ---
  const hexToRgb = (hex) => {
    let h = String(hex || "").trim().replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return [0, 0, 0];
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const rgbToHex = (r, g, b) =>
    "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
  const relLum = (rgb) => {
    const s = rgb.map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * s[0] + 0.7152 * s[1] + 0.0722 * s[2];
  };
  const contrastOf = (a, b) => {
    const l1 = relLum(hexToRgb(a)) + 0.05;
    const l2 = relLum(hexToRgb(b)) + 0.05;
    return Math.max(l1, l2) / Math.min(l1, l2);
  };
  const rgbToHsl = ([r, g, b]) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0; const l = (max + min) / 2;
    const d = max - min;
    if (d !== 0) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    return [h * 360, s * 100, l * 100];
  };
  const hslToHex = (h, s, l) => {
    h = ((h % 360) + 360) % 360; s /= 100; l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
  };
  // Prüft die eigene Palette auf zu geringen Kontrast / schlecht unterscheidbare Farben.
  const customColorWarning = (() => {
    if (!isCustomActive || !customColors) return null;
    const { c1, c2, c3 } = customColors;
    if (contrastOf(c2, c3) < 1.6)
      return "Farbe 2 und Farbe 3 sind sich zu ähnlich – die beiden Hintergründe lassen sich kaum unterscheiden.";
    if (Math.min(contrastOf(c1, c2), contrastOf(c1, c3)) < 1.35)
      return "Farbe 1 hebt sich kaum von einer der Hintergrundfarben ab und geht dort verloren.";
    return null;
  })();
  // Erzeugt einen harmonischen, gut lesbaren Vorschlag: Farbe 2 dunkel, Farbe 3 hell,
  // Farbe 1 als kräftiger Akzent – die Farbtöne des Nutzers bleiben erhalten.
  const suggestCustomColors = () => {
    if (!customColors) return;
    const [h1, s1] = rgbToHsl(hexToRgb(customColors.c1));
    const [h2, s2] = rgbToHsl(hexToRgb(customColors.c2));
    const [h3, s3] = rgbToHsl(hexToRgb(customColors.c3));
    const next = {
      c1: hslToHex(h1, Math.min(90, Math.max(45, s1)), 52),
      c2: hslToHex(h2, Math.min(55, Math.max(12, s2)), 13),
      c3: hslToHex(h3, Math.min(35, Math.max(6, s3)), 96),
    };
    setCustomColors((c) => ({ ...c, ...next }));
  };

  const textColorOptions = [
    { id: "auto", label: "Auto", key: "auto" },
    { id: "c1", label: "Farbe 1", key: "c1" },
    { id: "c2", label: "Farbe 2", key: "c2" },
    { id: "c3", label: "Farbe 3", key: "c3" },
  ];

  const orderedSectionKeys = useMemo(() => {
    const labelKeys = Object.keys(sectionLabels || {});
    const valid = (sectionOrder || []).filter((k) => labelKeys.includes(k));
    const missing = labelKeys.filter((k) => !valid.includes(k));
    return [...valid, ...missing];
  }, [sectionOrder, sectionLabels]);

  const hiddenSectionKeys = useMemo(
    () => orderedSectionKeys.filter((k) => sectionVisibility?.[k] === false),
    [orderedSectionKeys, sectionVisibility]
  );

  const showSection = (key) =>
    setSectionVisibility((prev) => ({ ...prev, [key]: true }));

  const moveSection = (key, dir) => {
    setSectionOrder((prev) => {
      const labelKeys = Object.keys(sectionLabels || {});
      const base = (prev && prev.length ? prev : labelKeys).filter((k) => labelKeys.includes(k));
      const withMissing = [...base, ...labelKeys.filter((k) => !base.includes(k))];
      const idx = withMissing.indexOf(key);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= withMissing.length) return withMissing;
      const next = [...withMissing];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const toggleSectionVisibility = (key) =>
    setSectionVisibility((prev) => ({
      ...prev,
      [key]: prev?.[key] === false ? true : false,
    }));

  const renderTextColorRow = (currentKey, onSelect) => (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {textColorOptions.map((opt) => {
        const active = (currentKey || "auto") === opt.key;
        return (
          <Button
            key={opt.id}
            size="small"
            variant={active ? "contained" : "outlined"}
            onClick={() => onSelect(opt.key)}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {opt.key !== "auto" && (
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: resolvePaletteColor(opt.key),
                    border: "1px solid rgba(0,0,0,0.2)",
                  }}
                />
              )}
              {opt.label}
            </Box>
          </Button>
        );
      })}
    </Stack>
  );

  const getPad = (key, side) => {
    const v = sectionPadding?.[key]?.[side];
    return v === undefined ? "auto" : v;
  };
  const setPad = (key, side, value) => {
    setSectionPadding((prev) => {
      const next = { ...(prev || {}) };
      const cur = { top: "auto", bottom: "auto", ...(next[key] || {}) };
      next[key] = { ...cur, [side]: value };
      return next;
    });
  };

  // Version A: einen Trenner (afterKey) bearbeiten. Typ + Abstand werden direkt
  // in der Vorschau am Trenner eingestellt (Hover-Toolbar).
  const DIVIDER_SPACING_MIN = 6;
  const DIVIDER_SPACING_MAX = 160;
  const clampSpacing = (v) =>
    Math.max(DIVIDER_SPACING_MIN, Math.min(DIVIDER_SPACING_MAX, Math.round(Number(v) || 0)));
  const updateDivider = (afterKey, patch) => {
    if (!setDividerConfig) return;
    setDividerConfig((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const cur = { type: "none", spacing: 26, ...(base[afterKey] || {}) };
      const nextEntry = { ...cur, ...patch };
      if (typeof nextEntry.spacing === "number") nextEntry.spacing = clampSpacing(nextEntry.spacing);
      return { ...base, [afterKey]: nextEntry };
    });
  };

  // Alle Trenner gleichzeitig ändern (rechte "Übergänge"-Sektion).
  const dividerConfigObj =
    dividerConfig && typeof dividerConfig === "object" ? dividerConfig : {};
  const setAllDividers = (patch) => {
    if (!setDividerConfig) return;
    setDividerConfig((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const next = { ...base };
      (orderedSectionKeys || []).forEach((key) => {
        const cur = { type: "none", spacing: 26, ...(base[key] || {}) };
        const entry = { ...cur, ...patch };
        if (typeof entry.spacing === "number") entry.spacing = clampSpacing(entry.spacing);
        next[key] = entry;
      });
      return next;
    });
  };
  const nudgeAllDividerSpacing = (delta) => {
    if (!setDividerConfig) return;
    setDividerConfig((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const next = { ...base };
      (orderedSectionKeys || []).forEach((key) => {
        const cur = { type: "none", spacing: 26, ...(base[key] || {}) };
        next[key] = { ...cur, spacing: clampSpacing(cur.spacing + delta) };
      });
      return next;
    });
  };
  // Gemeinsamer Trenner-Typ, falls alle gleich sind – sonst null (uneinheitlich).
  const commonDividerType = (() => {
    const keys = orderedSectionKeys || [];
    if (!keys.length) return "none";
    const types = keys.map((k) => dividerConfigObj[k]?.type || "none");
    const uniq = [...new Set(types)];
    return uniq.length === 1 ? uniq[0] : null;
  })();
  const avgDividerSpacing = (() => {
    const keys = orderedSectionKeys || [];
    if (!keys.length) return 26;
    const sum = keys.reduce(
      (acc, k) => acc + (Number(dividerConfigObj[k]?.spacing) || 26),
      0
    );
    return Math.round(sum / keys.length);
  })();

  // Direkt im Overlay editierbare Einzelelemente (Hover auf z. B. den Künstlernamen).
  const elementEditors = {
    name: {
      label: "Künstlername",
      size: nameFontSize,
      setSize: (v) => setNameFontSize(Math.max(40, Math.min(160, Math.round(v)))),
      step: 10,
      colorKey: nameColorKey,
      setColorKey: setNameColorKey,
      colorKeys: ["c1", "c2", "c3"],
    },
    bioImage: {
      label: "Portrait",
      size: bioImageScale ?? 1,
      setSize: (v) => setBioImageScale(Math.max(0.5, Math.min(1.6, Math.round(v * 10) / 10))),
      step: 0.2,
      sizeIcons: { dec: "−", inc: "+" },
      sizeLabels: { dec: "Kleiner", inc: "Größer" },
      rounding: {
        value: elementRoundness,
        setValue: setElementRoundness,
        options: elementRoundnessOptions,
      },
    },
  };

  // Geometrie der aktuell sichtbaren Vorschau-Seite für das Hover-Overlay
  const previewImgRef = useRef(null);
  const [imgBox, setImgBox] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  // Solange der native Farbwähler (freie Hintergrundfarbe) offen/fokussiert ist,
  // die Toolbar der Sektion offen halten: Der OS-Farbdialog liegt außerhalb der
  // Hover-Fläche -> mouseleave würde die Toolbar (und damit den Farbwähler-
  // Anker) sonst wegräumen, während man noch Farben auswählt.
  const [colorPickerKey, setColorPickerKey] = useState(null);
  // Hover mit kleiner Verzögerung schließen, damit der Weg von Element zu Menü
  // keinen "Gap" erzeugt, durch den das Menü verschwindet.
  const hoverTimer = useRef(null);
  const enterHover = (key) => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setHoverKey(key);
  };
  const leaveHover = (key) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setHoverKey((k) => (k === key ? null : k));
      hoverTimer.current = null;
    }, 220);
  };
  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); }, []);
  useLayoutEffect(() => {
    const el = previewImgRef.current;
    if (!el) return;
    const update = () => setImgBox({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [previewPages, previewIndex]);
  const currentGeometry = sectionGeometry?.[previewIndex] || null;

  // Auf welcher (Vorschau-)Seite landet welcher Abschnitt? Damit lässt sich im
  // "Abschnitte"-Panel visuell anzeigen, wo die Seiten umbrechen (Seitenwechsel).
  const sectionPageMap = useMemo(() => {
    const map = {};
    (sectionGeometry || []).forEach((pg, pageIdx) => {
      (pg?.sections || []).forEach((s) => {
        if (s && s.key != null && !(s.key in map)) map[s.key] = pageIdx;
      });
    });
    return map;
  }, [sectionGeometry]);

  // Wenn rechts eine Sektion offen ist, das zugehörige Element in der Vorschau
  // dauerhaft hervorheben (Box) und – für Einzelelemente – die Werkzeugleiste
  // einblenden, ohne dass gehovert werden muss.
  const { pinnedBoxKeys, pinnedToolbarKeys } = useMemo(() => {
    const box = new Set();
    const tools = new Set();
    if (expanded === "nameStyle") {
      box.add("el:name");
      tools.add("el:name");
    }
    if (expanded === "sections") {
      (currentGeometry?.sections || []).forEach((s) => box.add(s.key));
    }
    if (expanded === "dividers") {
      (currentGeometry?.dividers || []).forEach((d) => box.add(`div:${d.afterKey}`));
    }
    return { pinnedBoxKeys: box, pinnedToolbarKeys: tools };
  }, [expanded, currentGeometry]);

  // Maßstab, mit dem das 794×1123-Iframe (A4 @96dpi) in die gemessene
  // Vorschaufläche skaliert wird. imgBox kommt vom transparenten Sizer-Element,
  // das exakt das A4-Seitenverhältnis hat und sich wie das frühere <img> verhält.
  const previewScale = imgBox && imgBox.width ? imgBox.width / 794 : 0;
  // WICHTIG: Das Overlay MUSS mit exakt demselben Faktor skalieren wie das Iframe
  // (previewScale). Das Iframe wird immer als 794×1123-Box gerendert und bei mehr
  // Inhalt (mehrzeiliger Name -> wachsendes Split-Cover, vergrößertes Portrait)
  // oben abgeschnitten. Früher skalierte das Overlay mit imgBox.height/echte
  // Seitenhöhe – sobald die echte Höhe von 1123 abwich, drifteten Hover-Box und
  // sichtbarer Inhalt auseinander. Mit previewScale bleiben sie deckungsgleich.
  const overlayScale = previewScale;
  // Unsichtbarer Platzhalter mit A4-Intrinsic-Größe: skaliert sich exakt wie das
  // alte Vorschau-<img> (maxHeight/maxWidth, Seitenverhältnis) und liefert via
  // ResizeObserver die gerenderte Box-Größe für Maßstab + Overlay.
  const sizerSvg =
    "data:image/svg+xml," +
    encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' width='794' height='1123'></svg>"
    );

  const hairline = "1px solid rgba(2,6,23,0.10)";
  const softBorder = "1px solid rgba(2,6,23,0.12)";
  const pickRandomItem = (items, currentId) => {
    if (!items || items.length === 0) return null;
    const getId = (item) => (typeof item === "string" ? item : item.id);
    const filtered = items.filter((item) => getId(item) !== currentId);
    const pool = filtered.length ? filtered : items;
    return pool[Math.floor(Math.random() * pool.length)];
  };
  const pickRandomId = (items, currentId) => {
    const item = pickRandomItem(items, currentId);
    if (!item) return null;
    return typeof item === "string" ? item : item.id;
  };
  const handleRandomDesign = () => {
    const nextDesignId = pickRandomId(designTemplates, designId);
    if (nextDesignId) setDesignId(nextDesignId);
    const nextPaletteId = pickRandomId(colorTemplates, templateId);
    if (nextPaletteId) {
      setUseCustomColors(false);
      setTemplateId(nextPaletteId);
    }
    const nextBodyFontId = pickRandomId(bodyFontOptions || fontOptions, fontId);
    if (nextBodyFontId) setFontId(nextBodyFontId);
    const nextNameFontId = pickRandomId(fontOptions, nameFontId);
    if (nextNameFontId) setNameFontId(nextNameFontId);
    const nextTitleFontId = pickRandomId(fontOptions, titleFontId);
    if (nextTitleFontId) setTitleFontId(nextTitleFontId);
    const nextRoundness = pickRandomId(elementRoundnessOptions, elementRoundness);
    if (nextRoundness) setElementRoundness(nextRoundness);
    const sectionKeys = Object.keys(sectionLabels || {});
    if (sectionKeys.length && sectionColorModes?.length) {
      setSectionModes((prev) => {
        const next = { ...prev };
        sectionKeys.forEach((key) => {
          const modeId = pickRandomId(sectionColorModes, prev?.[key]);
          if (modeId) next[key] = modeId;
        });
        return next;
      });
    }
    // Trenner zufällig: nur ZWEI der möglichen Stile mischen, damit das Ergebnis
    // ruhig und stimmig bleibt (nicht jeder Trenner ein anderer Typ).
    if (setDividerConfig && sectionKeys.length) {
      const dividerPool = dividerTypeOptions
        .map((opt) => (typeof opt === "string" ? opt : opt.id))
        .filter((id) => id && id !== "none");
      if (dividerPool.length) {
        const first = dividerPool[Math.floor(Math.random() * dividerPool.length)];
        const rest = dividerPool.filter((id) => id !== first);
        const second = rest.length
          ? rest[Math.floor(Math.random() * rest.length)]
          : first;
        const mix = [first, second];
        setDividerConfig((prev) => {
          const base = prev && typeof prev === "object" ? prev : {};
          const next = { ...base };
          sectionKeys.forEach((key) => {
            const cur = { type: "none", spacing: 26, ...(base[key] || {}) };
            next[key] = { ...cur, type: mix[Math.floor(Math.random() * mix.length)] };
          });
          return next;
        });
      }
    }
  };

  const accordionSx = {
    bgcolor: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    "&:before": { display: "none" },

    // MUI default "expanded margin" killen (wichtig für clean)
    "&.Mui-expanded": { margin: 0 },
    "& .MuiCollapse-root": { background: "transparent" },
  };

  const accordionSummarySx = {
    // Überschriften werden jetzt über die Tab-Leiste gesteuert -> Summary ausblenden.
    display: "none",
    px: 0,
    minHeight: 48,
    "&.Mui-expanded": { minHeight: 48 },
    "& .MuiAccordionSummary-content": {
      my: 0.75,
      alignItems: "center",
    },
    // Einheitliche, dezente Sektions-Labels
    "& .MuiAccordionSummary-content .MuiTypography-root": {
      fontSize: "0.9rem",
      fontWeight: 600,
      letterSpacing: "0.005em",
      color: "rgba(15,23,42,0.85)",
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      color: "rgba(15,23,42,0.35)",
      transition: "color 120ms ease",
    },
    "&:hover .MuiAccordionSummary-expandIconWrapper": {
      color: "rgba(15,23,42,0.6)",
    },
  };

  const accordionDetailsSx = {
    px: 0,
    pt: 0,
    pb: 2.5,
  };
  const previewMaxHeight = {
    xs: "calc(100vh - 150px)",
    sm: "calc(100vh - 130px)",
    md: "calc(100vh - 118px)",
  };

  const paletteTileSx = (active) => ({
    cursor: "pointer",
    borderRadius: 2,
    border: active ? `1.5px solid ${accentColor}` : softBorder,
    bgcolor: active ? "rgba(125,156,20,0.06)" : "transparent",
    px: 1,
    py: 0.9,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    transition: "background 120ms ease, border-color 120ms ease",
    "&:hover": { bgcolor: "rgba(2,6,23,0.03)" },
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box
        sx={{
          height: { xs: "auto", md: "100%" },
          minHeight: "100%",
          display: { xs: "block", md: "grid" },
          gridTemplateColumns: {
            xs: "1fr",
            md: `${previewPanePct}% ${100 - previewPanePct}%`,
          },
          position: "relative",
          overflowY: { xs: "auto", md: "hidden" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            color: "#fff",
            minHeight: 0,
            background:
              "radial-gradient(900px 520px at 10% 10%, rgba(163,216,30,0.35), transparent 60%), radial-gradient(800px 520px at 90% 12%, rgba(163,216,30,0.28), transparent 65%), linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))",
            backdropFilter: "blur(18px) saturate(140%)",
            borderRight: { xs: "none", md: "1px solid rgba(255,255,255,0.12)" },
          }}
        >
          <Box
            sx={{
              flex: 1,
              p: { xs: 2, md: 3 },
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              minHeight: { xs: "auto", md: "auto" },
              overflow: { xs: "visible", md: "auto" },
            }}
          >
            <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
              {previewError ? (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                  {previewError}
                </Typography>
              ) : previewPages.length ? (
                <Box
                  sx={{
                    width: "auto",
                    maxWidth: "100%",
                    maxHeight: previewMaxHeight,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    borderRadius: 1,
                    boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
                    overflow: "visible",
                    mx: "auto",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setPreviewIndex((i) => Math.max(i - 1, 0))}
                    disabled={previewIndex === 0 || previewLoading || previewPages.length === 0}
                    sx={{
                      position: "absolute",
                      left: -40,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(15,23,42,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(15,23,42,0.75)" },
                      display:
                        previewIndex === 0 || previewLoading || previewPages.length === 0
                          ? "none"
                          : "inline-flex",
                    }}
                    aria-label="Vorherige Seite"
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setPreviewIndex((i) => Math.min(i + 1, Math.max(previewPages.length - 1, 0)))
                    }
                    disabled={previewIndex >= previewPages.length - 1 || previewLoading || previewPages.length === 0}
                    sx={{
                      position: "absolute",
                      right: -40,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(15,23,42,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(15,23,42,0.75)" },
                      display:
                        previewIndex >= previewPages.length - 1 ||
                        previewLoading ||
                        previewPages.length === 0
                          ? "none"
                          : "inline-flex",
                    }}
                    aria-label="Nächste Seite"
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: "#fff",
                      maxHeight: previewMaxHeight,
                      maxWidth: "100%",
                    }}
                  >
                    <Box
                      component="img"
                      ref={previewImgRef}
                      src={sizerSvg}
                      alt=""
                      aria-hidden="true"
                      sx={{
                        maxHeight: previewMaxHeight,
                        maxWidth: "100%",
                        width: "auto",
                        height: "auto",
                        display: "block",
                      }}
                    />
                    <Box
                      component="iframe"
                      key={previewIndex}
                      title={`PDF Seite ${previewIndex + 1}`}
                      srcDoc={previewPages[previewIndex]}
                      scrolling="no"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "794px",
                        height: "1123px",
                        border: 0,
                        transformOrigin: "top left",
                        transform: previewScale ? `scale(${previewScale})` : "scale(0)",
                        visibility: previewScale ? "visible" : "hidden",
                        pointerEvents: "none",
                      }}
                    />
                    {!previewLoading && overlayScale > 0 &&
                    (currentGeometry?.sections?.length || currentGeometry?.elements?.length || currentGeometry?.dividers?.length) ? (
                      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                        {(currentGeometry.sections || []).map((sec) => {
                          const top = sec.top * overlayScale;
                          const height = Math.max(0, sec.height * overlayScale);
                          const active =
                            hoverKey === sec.key ||
                            pinnedBoxKeys.has(sec.key) ||
                            pinnedToolbarKeys.has(sec.key) ||
                            colorPickerKey === sec.key;
                          const showTools =
                            hoverKey === sec.key ||
                            pinnedToolbarKeys.has(sec.key) ||
                            colorPickerKey === sec.key;
                          return (
                            <Box
                              key={sec.key}
                              onMouseEnter={() => enterHover(sec.key)}
                              onMouseLeave={() => leaveHover(sec.key)}
                              onClick={() => setExpanded("sections")}
                              sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top,
                                height,
                                pointerEvents: "auto",
                                cursor: "default",
                                border: active ? "2px solid rgba(125,156,20,0.9)" : "2px solid transparent",
                                bgcolor: active ? "rgba(125,156,20,0.06)" : "transparent",
                                transition: "background 120ms ease, border-color 120ms ease",
                                boxSizing: "border-box",
                              }}
                            >
                              {showTools && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  onMouseEnter={() => enterHover(sec.key)}
                                  onMouseLeave={() => leaveHover(sec.key)}
                                  sx={{
                                    position: "absolute",
                                    top: 6,
                                    right: 6,
                                    bgcolor: "rgba(15,23,42,0.92)",
                                    borderRadius: 999,
                                    px: 0.75,
                                    py: 0.4,
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                                    flexWrap: "nowrap",
                                    whiteSpace: "nowrap",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Typography
                                    sx={{ color: "#fff", fontSize: 11, fontWeight: 700, px: 0.5, whiteSpace: "nowrap" }}
                                  >
                                    {sectionLabels[sec.key] || sec.key}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setSectionVisibility((prev) => ({ ...prev, [sec.key]: false }))
                                    }
                                    sx={{
                                      color: "#fff",
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      border: "1px solid rgba(255,255,255,0.35)",
                                      bgcolor: "rgba(255,255,255,0.08)",
                                      "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                                    }}
                                    aria-label="Sektion ausblenden"
                                    title="Sektion ausblenden"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveSection(sec.key, -1)}
                                    sx={{
                                      color: "#fff",
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      border: "1px solid rgba(255,255,255,0.35)",
                                      bgcolor: "rgba(255,255,255,0.08)",
                                      "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                                    }}
                                    aria-label="Sektion nach oben"
                                  >
                                    <KeyboardArrowUpIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveSection(sec.key, 1)}
                                    sx={{
                                      color: "#fff",
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      border: "1px solid rgba(255,255,255,0.35)",
                                      bgcolor: "rgba(255,255,255,0.08)",
                                      "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                                    }}
                                    aria-label="Sektion nach unten"
                                  >
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  </IconButton>
                                  {sectionColorModes.map((mode) => {
                                    const isOn = sectionModes[sec.key] === mode.id;
                                    return (
                                      <Box
                                        key={mode.id}
                                        role="button"
                                        onClick={() =>
                                          setSectionModes((prev) => ({ ...prev, [sec.key]: mode.id }))
                                        }
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: "50%",
                                          bgcolor: palettePreview?.[mode.id],
                                          border: isOn ? "2px solid #fff" : "2px solid rgba(255,255,255,0.4)",
                                          cursor: "pointer",
                                          flexShrink: 0,
                                        }}
                                      />
                                    );
                                  })}
                                  {(() => {
                                    const cur = sectionModes[sec.key];
                                    const isCustom = typeof cur === "string" && cur.startsWith("#");
                                    return (
                                      <Box
                                        component="label"
                                        title="Freie Hintergrundfarbe"
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: "50%",
                                          flexShrink: 0,
                                          cursor: "pointer",
                                          position: "relative",
                                          overflow: "hidden",
                                          border: isCustom ? "2px solid #fff" : "2px solid rgba(255,255,255,0.4)",
                                          background:
                                            isCustom
                                              ? cur
                                              : "conic-gradient(#f43f5e,#f59e0b,#22c55e,#0ea5e9,#7d9c14,#f43f5e)",
                                        }}
                                      >
                                        <Box
                                          component="input"
                                          type="color"
                                          value={isCustom ? cur : "#0ea5e9"}
                                          onFocus={() => setColorPickerKey(sec.key)}
                                          onBlur={() =>
                                            setColorPickerKey((k) => (k === sec.key ? null : k))
                                          }
                                          onChange={(e) =>
                                            setSectionModes((prev) => ({ ...prev, [sec.key]: e.target.value }))
                                          }
                                          sx={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", border: 0, p: 0 }}
                                        />
                                      </Box>
                                    );
                                  })()}
                                  {(() => {
                                    // "A" zeigt die (automatische) Textfarbe des Abschnitts.
                                    const cur = sectionModes[sec.key];
                                    const bg =
                                      typeof cur === "string" && cur.startsWith("#")
                                        ? cur
                                        : palettePreview?.[cur] || palettePreview?.c3 || "#ffffff";
                                    const parseRgb = (c) => {
                                      if (!c) return [255, 255, 255];
                                      if (c.startsWith("#")) return hexToRgb(c);
                                      const m = c.match(/(\d+\.?\d*)/g);
                                      return m ? [+m[0], +m[1], +m[2]] : [255, 255, 255];
                                    };
                                    const txt = relLum(parseRgb(bg)) > 0.4 ? "#111111" : "#ffffff";
                                    return (
                                      <Box
                                        title="Textfarbe (automatisch, bester Kontrast)"
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: "50%",
                                          flexShrink: 0,
                                          bgcolor: bg,
                                          border: "2px solid rgba(255,255,255,0.4)",
                                          display: "grid",
                                          placeItems: "center",
                                        }}
                                      >
                                        <Typography sx={{ fontSize: 10, fontWeight: 900, color: txt, lineHeight: 1 }}>
                                          A
                                        </Typography>
                                      </Box>
                                    );
                                  })()}
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                        {(currentGeometry.elements || []).map((el, elIdx) => {
                          const cfg = elementEditors[el.key];
                          // Klick auf ein Vorschau-Element öffnet rechts direkt den
                          // passenden Einstellungs-Tab (Bild -> Bilder, Text -> Text, …).
                          const targetTab =
                            {
                              name: "nameStyle",
                              bioImage: "images",
                              heroImage: "images",
                              sectionTitle: "titleStyle",
                              sectionBody: "bodyStyle",
                              coverText: "bodyStyle",
                            }[el.key] || "front";
                          const left = el.left * overlayScale;
                          const top = el.top * overlayScale;
                          const width = Math.max(0, el.width * overlayScale);
                          const height = Math.max(0, el.height * overlayScale);
                          // Elemente mit Toolbar (name, bioImage) brauchen stabile Keys
                          // (Pinning), die übrigen können mehrfach vorkommen -> Index.
                          const hk = cfg ? `el:${el.key}` : `el:${el.key}:${elIdx}`;
                          const active =
                            hoverKey === hk || pinnedBoxKeys.has(hk) || pinnedToolbarKeys.has(hk);
                          const showTools = !!cfg && (hoverKey === hk || pinnedToolbarKeys.has(hk));
                          // Toolbar an der Seite verankern, die im Blatt liegt: sitzt das
                          // Element rechts, wächst die Leiste nach links (right:0), damit
                          // sie nicht über den rechten Rand hinausragt.
                          const anchorRight = el.left + el.width / 2 > 397;
                          return (
                            <Box
                              key={hk}
                              onMouseEnter={() => enterHover(hk)}
                              onMouseLeave={() => leaveHover(hk)}
                              onClick={() => setExpanded(targetTab)}
                              sx={{
                                position: "absolute",
                                left,
                                top,
                                width,
                                height,
                                pointerEvents: "auto",
                                cursor: "pointer",
                                borderRadius: "3px",
                                border: active ? "2px solid rgba(125,156,20,0.9)" : "2px solid transparent",
                                bgcolor: active ? "rgba(125,156,20,0.06)" : "transparent",
                                transition: "background 120ms ease, border-color 120ms ease",
                                boxSizing: "border-box",
                              }}
                            >
                              {showTools && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  onMouseEnter={() => enterHover(hk)}
                                  onMouseLeave={() => leaveHover(hk)}
                                  sx={{
                                    position: "absolute",
                                    bottom: "100%",
                                    left: anchorRight ? "auto" : 0,
                                    right: anchorRight ? 0 : "auto",
                                    flexWrap: "nowrap",
                                    whiteSpace: "nowrap",
                                    justifyContent: anchorRight ? "flex-end" : "flex-start",
                                    mb: "-2px",
                                    pb: "12px",
                                    bgcolor: "rgba(15,23,42,0.92)",
                                    borderRadius: 999,
                                    px: 0.75,
                                    py: 0.4,
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                                    zIndex: 2,
                                    // unsichtbare Brücke nach unten, damit der Weg ins Menü keinen Gap hat
                                    "&::after": {
                                      content: '""',
                                      position: "absolute",
                                      left: 0,
                                      right: 0,
                                      top: "100%",
                                      height: "16px",
                                    },
                                  }}
                                >
                                  <Typography
                                    sx={{ color: "#fff", fontSize: 11, fontWeight: 700, px: 0.5, whiteSpace: "nowrap" }}
                                  >
                                    {cfg.label}
                                  </Typography>
                                  {cfg.setSize && (
                                    <>
                                      <IconButton
                                        size="small"
                                        onClick={() => cfg.setSize(cfg.size - cfg.step)}
                                        sx={{
                                          color: "#fff",
                                          width: 24,
                                          height: 24,
                                          borderRadius: "50%",
                                          border: "1px solid rgba(255,255,255,0.35)",
                                          bgcolor: "rgba(255,255,255,0.08)",
                                          "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                                        }}
                                        aria-label={cfg.sizeLabels?.dec || "Kleiner"}
                                        title={cfg.sizeLabels?.dec || "Kleiner"}
                                      >
                                        <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 800, lineHeight: 1 }}>
                                          {cfg.sizeIcons?.dec || "A−"}
                                        </Typography>
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => cfg.setSize(cfg.size + cfg.step)}
                                        sx={{
                                          color: "#fff",
                                          width: 24,
                                          height: 24,
                                          borderRadius: "50%",
                                          border: "1px solid rgba(255,255,255,0.35)",
                                          bgcolor: "rgba(255,255,255,0.08)",
                                          "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                                        }}
                                        aria-label={cfg.sizeLabels?.inc || "Größer"}
                                        title={cfg.sizeLabels?.inc || "Größer"}
                                      >
                                        <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 800, lineHeight: 1 }}>
                                          {cfg.sizeIcons?.inc || "A+"}
                                        </Typography>
                                      </IconButton>
                                    </>
                                  )}
                                  {(cfg.colorKeys || []).map((ck) => {
                                    const isOn = cfg.colorKey === ck;
                                    return (
                                      <Box
                                        key={ck}
                                        role="button"
                                        onClick={() => cfg.setColorKey(ck)}
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: "50%",
                                          bgcolor: palettePreview?.[ck],
                                          border: isOn ? "2px solid #fff" : "2px solid rgba(255,255,255,0.4)",
                                          cursor: "pointer",
                                          flexShrink: 0,
                                        }}
                                      />
                                    );
                                  })}
                                  {cfg.rounding && (cfg.rounding.options || []).map((opt) => {
                                    const isOn = cfg.rounding.value === opt.id;
                                    return (
                                      <Box
                                        key={opt.id}
                                        role="button"
                                        onClick={() => cfg.rounding.setValue(opt.id)}
                                        title={`Ecken: ${opt.label}`}
                                        aria-label={`Ecken: ${opt.label}`}
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: 24,
                                          height: 24,
                                          borderRadius: 999,
                                          cursor: "pointer",
                                          color: isOn ? "#0b1020" : "rgba(255,255,255,0.85)",
                                          bgcolor: isOn ? "#fff" : "rgba(255,255,255,0.12)",
                                          flexShrink: 0,
                                          "&:hover": { bgcolor: isOn ? "#fff" : "rgba(255,255,255,0.22)" },
                                        }}
                                      >
                                        <RoundingIcon id={opt.id} />
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                        {(currentGeometry.dividers || []).map((dv) => {
                          const rawTop = dv.top * overlayScale;
                          const rawHeight = Math.max(0, dv.height * overlayScale);
                          // Mindest-Trefferfläche, damit auch dünne Trenner gut greifbar sind.
                          const hitHeight = Math.max(rawHeight, 16);
                          const top = rawTop + rawHeight / 2 - hitHeight / 2;
                          const hk = `div:${dv.afterKey}`;
                          const active = hoverKey === hk || pinnedBoxKeys.has(hk);
                          const showTools = hoverKey === hk;
                          // "Minimaler" Übergang -> dünne Linie zum Anfassen genügt.
                          // Echter Block (spürbare Höhe) -> ganzer Block ist die Hover-Fläche
                          // und wird beim Hover als Block hervorgehoben.
                          const isBlock = rawHeight >= 18;
                          const activeBefore = isBlock
                            ? {
                                content: '""',
                                position: "absolute",
                                inset: 0,
                                borderRadius: "3px",
                                bgcolor: active ? "rgba(125,156,20,0.16)" : "transparent",
                                boxShadow: active ? "inset 0 0 0 1.5px rgba(125,156,20,0.9)" : "none",
                              }
                            : {
                                content: '""',
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                height: active ? 2 : 0,
                                bgcolor: "rgba(125,156,20,0.9)",
                              };
                          return (
                            <Box
                              key={hk}
                              onMouseEnter={() => enterHover(hk)}
                              onMouseLeave={() => leaveHover(hk)}
                              onClick={() => setExpanded("dividers")}
                              sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top,
                                height: hitHeight,
                                pointerEvents: "auto",
                                cursor: "ns-resize",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxSizing: "border-box",
                                zIndex: 2,
                                "&::before": activeBefore,
                              }}
                            >
                              {showTools && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  onMouseEnter={() => enterHover(hk)}
                                  onMouseLeave={() => leaveHover(hk)}
                                  sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    bgcolor: "rgba(15,23,42,0.94)",
                                    borderRadius: 1.5,
                                    px: 0.75,
                                    py: 0.4,
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                                    flexWrap: "nowrap",
                                    maxWidth: "none",
                                    justifyContent: "center",
                                    zIndex: 3,
                                  }}
                                >
                                  <Typography
                                    sx={{ color: "#fff", fontSize: 11, fontWeight: 700, px: 0.5, whiteSpace: "nowrap" }}
                                  >
                                    Trenner
                                  </Typography>
                                  {dividerTypeOptions.map((opt) => {
                                    const isOn = dv.type === opt.id;
                                    return (
                                      <Box
                                        key={opt.id}
                                        role="button"
                                        onClick={() => updateDivider(dv.afterKey, { type: opt.id })}
                                        title={opt.label}
                                        aria-label={opt.label}
                                        sx={{
                                          width: 30,
                                          height: 24,
                                          borderRadius: 999,
                                          cursor: "pointer",
                                          color: isOn ? "#0b1020" : "rgba(255,255,255,0.85)",
                                          bgcolor: isOn ? "#fff" : "rgba(255,255,255,0.12)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flex: "0 0 auto",
                                          "&:hover": { bgcolor: isOn ? "#fff" : "rgba(255,255,255,0.22)" },
                                        }}
                                      >
                                        <DividerTypeIcon type={opt.id} />
                                      </Box>
                                    );
                                  })}
                                  <Box sx={{ width: "1px", height: 16, bgcolor: "rgba(255,255,255,0.2)", mx: 0.3 }} />
                                  <IconButton
                                    size="small"
                                    onClick={() => updateDivider(dv.afterKey, { spacing: dv.spacing - dividerSpacingStep })}
                                    sx={{
                                      color: "#fff",
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      border: "1px solid rgba(255,255,255,0.35)",
                                      bgcolor: "rgba(255,255,255,0.08)",
                                      "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                                    }}
                                    aria-label="Abstand kleiner"
                                    title="Abstand kleiner"
                                  >
                                    <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 800, lineHeight: 1 }}>−</Typography>
                                  </IconButton>
                                  <Typography sx={{ color: "#fff", fontSize: 11, fontWeight: 700, minWidth: 30, textAlign: "center" }}>
                                    {Math.round(dv.spacing)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => updateDivider(dv.afterKey, { spacing: dv.spacing + dividerSpacingStep })}
                                    sx={{
                                      color: "#fff",
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      border: "1px solid rgba(255,255,255,0.35)",
                                      bgcolor: "rgba(255,255,255,0.08)",
                                      "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                                    }}
                                    aria-label="Abstand größer"
                                    title="Abstand größer"
                                  >
                                    <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 800, lineHeight: 1 }}>+</Typography>
                                  </IconButton>
                                </Stack>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                  Noch keine Vorschau.
                </Typography>
              )}
              {previewPages.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ flex: "0 0 auto" }}
                >
                  <Box
                    sx={{
                      px: 1,
                      py: 0.35,
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.35)",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      bgcolor: "rgba(15,23,42,0.35)",
                    }}
                  >
                    {currentPage}/{totalPages}
                  </Box>
                  <IconButton
                    onClick={onRefreshPreview}
                    disabled={previewLoading}
                    size="small"
                    sx={{
                      bgcolor: "rgba(15,23,42,0.45)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(15,23,42,0.65)" },
                    }}
                    aria-label="Vorschau neu laden"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onDownloadPdf}
                    disabled={previewLoading || pdfLoading || !isPro}
                    startIcon={
                      pdfLoading ? (
                        <CircularProgress size={15} thickness={5} sx={{ color: "#14150c" }} />
                      ) : (
                        <FileDownloadIcon sx={{ fontSize: 17 }} />
                      )
                    }
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      px: 1.5,
                      color: "#14150c",
                      background: "linear-gradient(135deg, #c3f04c 0%, #a3d81e 100%)",
                      boxShadow: "0 8px 20px rgba(23,23,15,0.32)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #94c716 0%, #94c716 100%)",
                      },
                      "&.Mui-disabled": { color: "rgba(20,21,12,0.6)", opacity: 0.75 },
                    }}
                  >
                    {pdfLoading ? "Erstellt…" : "PDF"}
                  </Button>
                  {!isPro && (
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", ml: 0.5 }}>
                      Download nur mit Premium.
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>

        <Box
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            const startX = e.clientX;
            const startPct = previewPanePct;
            const container = e.currentTarget.parentElement;
            if (!container) return;
            const totalWidth = container.getBoundingClientRect().width;

            const onMove = (ev) => {
              const delta = ev.clientX - startX;
              const pctDelta = (delta / totalWidth) * 100;
              const next = Math.min(65, Math.max(35, startPct + pctDelta));
              setPreviewPanePct(Number(next.toFixed(2)));
            };
            const onUp = () => {
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup", onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          }}
          sx={{
            display: { xs: "none", md: "block" },
            position: "absolute",
            left: `calc(${previewPanePct}% - 6px)`,
            top: 0,
            bottom: 0,
            width: 12,
            cursor: "col-resize",
            zIndex: 5,
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: 2,
              mx: "auto",
              background: "rgba(255,255,255,0.15)",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
            }}
          />
        </Box>

        <Box
          sx={{
            bgcolor: "#fff",
            color: "text.primary",
            overflowY: { xs: "visible", md: "auto" },
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack spacing={2.6}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "rgba(15,23,42,0.5)",
                }}
              >
                Design Editor
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleRandomDesign}
                    startIcon={<AutoFixHighIcon sx={{ fontSize: 17 }} />}
                    sx={{
                      borderRadius: "999px 0 0 999px",
                      textTransform: "none",
                      fontWeight: 600,
                      color: "rgba(15,23,42,0.7)",
                      pl: 1.25,
                      pr: 0.75,
                      minWidth: 0,
                      "&:hover": { bgcolor: "rgba(15,23,42,0.05)" },
                    }}
                  >
                    Zufall
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => setContentMenuAnchor(e.currentTarget)}
                    aria-label="Mehr Optionen"
                    sx={{
                      color: "rgba(15,23,42,0.7)",
                      borderRadius: "0 999px 999px 0",
                      p: 0.4,
                      "&:hover": { bgcolor: "rgba(15,23,42,0.05)" },
                    }}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
                <Menu
                  anchorEl={contentMenuAnchor}
                  open={Boolean(contentMenuAnchor)}
                  onClose={() => setContentMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => { setContentMenuAnchor(null); handleRandomDesign(); }}
                  >
                    Zufälliges Design
                  </MenuItem>
                  <MenuItem
                    disabled={!onFillMinimum}
                    onClick={() => { setContentMenuAnchor(null); onFillMinimum && onFillMinimum(); }}
                  >
                    Text: Minimum (ein Wort)
                  </MenuItem>
                  <MenuItem
                    disabled={!onFillMaximum}
                    onClick={() => { setContentMenuAnchor(null); onFillMaximum && onFillMaximum(); }}
                  >
                    Text: Maximum (volle Länge)
                  </MenuItem>
                </Menu>
                {onFillLorem && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={onFillLorem}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 600,
                      color: "rgba(15,23,42,0.7)",
                      px: 1.25,
                      "&:hover": { bgcolor: "rgba(15,23,42,0.05)" },
                    }}
                  >
                    Lorem
                  </Button>
                )}
                <IconButton
                  size="small"
                  onClick={onClose}
                  aria-label="Schliessen"
                  sx={{
                    color: "rgba(15,23,42,0.55)",
                    "&:hover": { bgcolor: "rgba(15,23,42,0.06)", color: "rgba(15,23,42,0.9)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            {showEditHint && (
              <Box
                sx={{
                  mb: 1.25,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 2.5,
                  border: "1px solid rgba(125,156,20,0.35)",
                  background: "linear-gradient(135deg, rgba(125,156,20,0.10), rgba(195,240,76,0.10))",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  animation: "mkHintPulse 1.8s ease-in-out infinite",
                  "@keyframes mkHintPulse": {
                    "0%,100%": { boxShadow: "0 0 0 0 rgba(125,156,20,0.0)" },
                    "50%": { boxShadow: "0 0 0 5px rgba(125,156,20,0.14)" },
                  },
                }}
              >
                <AutoFixHighIcon sx={{ color: accentColor, fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, color: "rgba(15,23,42,0.85)", fontWeight: 500 }}>
                  Fahre in der Vorschau mit der Maus über die Elemente, um sie direkt zu bearbeiten.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={dismissEditHint}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700, flexShrink: 0, bgcolor: accentColor }}
                >
                  Verstanden
                </Button>
              </Box>
            )}

            <Tabs
              value={expanded || "colors"}
              onChange={(e, v) => handleAccordionChange(v)(e, true)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 44,
                borderBottom: hairline,
                mb: 1,
                "& .MuiTabs-indicator": { height: 3, borderRadius: 3, bgcolor: accentColor },
                "& .MuiTab-root": {
                  minHeight: 44,
                  minWidth: 0,
                  px: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  color: "rgba(15,23,42,0.6)",
                },
                "& .Mui-selected": { color: "rgba(15,23,42,0.95)" },
              }}
            >
              {[
                { id: "colors", label: "Farben", icon: <PaletteIcon fontSize="small" />, color: "#f43f5e" },
                { id: "front", label: "Design", icon: <DashboardCustomizeIcon fontSize="small" />, color: "#7d9c14" },
                { id: "images", label: "Bilder", icon: <CropIcon fontSize="small" />, color: "#94c716" },
                { id: "sections", label: "Abschnitte", icon: <ViewAgendaIcon fontSize="small" />, color: "#0ea5e9" },
                { id: "dividers", label: "Übergänge", icon: <SwapVertIcon fontSize="small" />, color: "#14b8a6" },
                { id: "nameStyle", label: "Name", icon: <BadgeIcon fontSize="small" />, color: "#f59e0b" },
                { id: "titleStyle", label: "Titel", icon: <TitleIcon fontSize="small" />, color: "#a3d81e" },
                { id: "bodyStyle", label: "Text", icon: <NotesIcon fontSize="small" />, color: "#22c55e" },
              ].map((t) => (
                <Tab
                  key={t.id}
                  value={t.id}
                  iconPosition="start"
                  icon={React.cloneElement(t.icon, {
                    sx: { color: expanded === t.id ? t.color : "rgba(15,23,42,0.4)", mr: 0.25 },
                  })}
                  label={t.label}
                />
              ))}
            </Tabs>

            <Stack spacing={1.5}>
              <Accordion
                expanded={expanded === "colors"}
                onChange={handleAccordionChange("colors")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Typography sx={{ fontWeight: 700 }}>Farben</Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Stack spacing={2}>
                    <Box>
                      <Grid container spacing={1.5}>
                        {colorTemplates.map((t) => {
                          const active = !isCustomActive && templateId === t.id;
                          return (
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }} key={t.id}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setTemplateId(t.id);
                                  setUseCustomColors(false);
                                }}
                                sx={{
                                  cursor: "pointer",
                                  borderRadius: 2.5,
                                  borderWidth: 1,
                                  borderColor: active ? accentColor : "rgba(15,23,42,0.08)",
                                  boxShadow: active ? `0 0 0 1px ${accentColor}` : "none",
                                  bgcolor: active ? "rgba(125,156,20,0.04)" : "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  px: 1.5,
                                  py: 1.25,
                                  transition: "border-color 120ms ease, box-shadow 120ms ease, background 120ms ease",
                                  "&:hover": {
                                    borderColor: active ? accentColor : "rgba(15,23,42,0.18)",
                                    bgcolor: active ? "rgba(125,156,20,0.04)" : "rgba(15,23,42,0.02)",
                                  },
                                }}
                              >
                                <Stack spacing={1} alignItems="center">
                                  <Stack direction="row" spacing={0.6}>
                                    <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: t.c1 }} />
                                    <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: t.c2, ml: "-6px" }} />
                                    <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: t.c3, ml: "-6px" }} />
                                  </Stack>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "0.8rem",
                                      color: active ? "rgba(15,23,42,0.9)" : "rgba(15,23,42,0.7)",
                                    }}
                                  >
                                    {t.name}
                                  </Typography>
                                </Stack>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant={isCustomActive ? "contained" : "outlined"}
                        onClick={() => setUseCustomColors((v) => !v)}
                        sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                      >
                        Benutzerdefiniert
                      </Button>
                      {isCustomActive && (
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <Box sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: customColors.c1 }} />
                          <Box sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: customColors.c2 }} />
                          <Box sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: customColors.c3 }} />
                        </Stack>
                      )}
                    </Stack>

                    {isCustomActive && (
                      <Box sx={{ border: "1px solid rgba(2,6,23,0.12)", borderRadius: 2, p: 1.5 }}>
                        <Grid container spacing={1.5}>
                          {[
                            { label: "Farbe 1", key: "c1" },
                            { label: "Farbe 2", key: "c2" },
                            { label: "Farbe 3", key: "c3" },
                          ].map((col) => (
                            <Grid size={{ xs: 12, sm: 4 }} key={col.key}>
                              <Stack spacing={1}>
                                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: "0.02em" }}>
                                  {col.label}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <input
                                    type="color"
                                    value={customColors[col.key]}
                                    onChange={(e) => setCustomColors((c) => ({ ...c, [col.key]: e.target.value }))}
                                    style={{ width: 44, height: 32, border: "1px solid rgba(0,0,0,0.2)", borderRadius: 8, background: "transparent" }}
                                  />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <input
                                      value={customColors[col.key]}
                                      onChange={(e) => setCustomColors((c) => ({ ...c, [col.key]: e.target.value }))}
                                      style={{
                                        width: "100%",
                                        height: 32,
                                        borderRadius: 8,
                                        border: "1px solid rgba(0,0,0,0.2)",
                                        padding: "0 8px",
                                      }}
                                    />
                                  </Box>
                                </Stack>
                              </Stack>
                            </Grid>
                          ))}
                        </Grid>
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.25 }}>
                          Die Textfarbe auf jeder Farbe wird automatisch für beste Lesbarkeit gewählt.
                        </Typography>
                        {customColorWarning && (
                          <Box
                            sx={{
                              mt: 1.25,
                              p: 1.25,
                              borderRadius: 2,
                              border: "1px solid rgba(217,119,6,0.4)",
                              bgcolor: "rgba(217,119,6,0.08)",
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "rgba(146,64,14,0.95)", fontWeight: 600, lineHeight: 1.4 }}>
                              ⚠ {customColorWarning}
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={suggestCustomColors}
                              startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 700,
                                bgcolor: "#d97706",
                                "&:hover": { bgcolor: "#b45309" },
                              }}
                            >
                              Vorschlag übernehmen
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}

                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "front"}
                onChange={handleAccordionChange("front")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Typography sx={{ fontWeight: 700 }}>Designvorlagen</Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Grid container spacing={1.5}>

                    {designTemplates.map((t) => {
                      const active = designId === t.id;
                      return (
                        // 4 Spalten (xs: 3): kompakte, einheitliche A4-Kacheln —
                        // volle Breite wirkte nach der Umstellung auf echtes
                        // A4-Seitenverhältnis viel zu groß.
                        <Grid size={{ xs: 4, sm: 3 }} key={t.id}>
                          <Card
                            variant="outlined"
                            onClick={() => setDesignId(t.id)}
                            sx={{
                              cursor: "pointer",
                              borderWidth: 2,
                              borderColor: active ? accentColor : "rgba(0,0,0,0.12)",
                              overflow: "hidden",
                              height: "100%",
                            }}
                          >
                            {/* Feste A4-Proportion pro Kachel: alle Vorschauen sind exakt
                                gleich groß und skalieren beim Fenster-Resize einheitlich mit. */}
                            <Box
                              sx={{
                                width: "100%",
                                aspectRatio: "210/297",
                                bgcolor: "rgba(0,0,0,0.05)",
                              }}
                            >
                              <Box
                                component="img"
                                src={t.preview}
                                alt={`Vorschau ${t.name}`}
                                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                              />
                            </Box>
                            <Box sx={{ px: 1, py: 0.8 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  textAlign: "center",
                                  lineHeight: 1.2,
                                  display: "block",
                                }}
                              >
                                {t.name}
                              </Typography>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                   
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      CTA Anzeige
                    </Typography>
                    <Stack spacing={0.5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={titleCtaLinkEnabled}
                            onChange={(e) => setTitleCtaLinkEnabled(e.target.checked)}
                          />
                        }
                        label="Link anzeigen"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={titleCtaQrEnabled}
                            onChange={(e) => setTitleCtaQrEnabled(e.target.checked)}
                          />
                        }
                        label="QR-Code anzeigen"
                      />
                    </Stack>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Footer
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={footerEnabled}
                          onChange={(e) => setFooterEnabled(e.target.checked)}
                        />
                      }
                      label="Footer anzeigen"
                    />
                  </Box>

                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "images"}
                onChange={handleAccordionChange("images")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Typography sx={{ fontWeight: 700 }}>Bilder</Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Zuschneiden
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                        Ausschnitt & Zoom deiner Fotos festlegen.
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CropIcon fontSize="small" />}
                          disabled={!hasHeroPhoto}
                          onClick={onOpenHeroCrop}
                          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                        >
                          Titelbild zuschneiden
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CropIcon fontSize="small" />}
                          disabled={!hasPortraitPhoto}
                          onClick={onOpenPortraitCrop}
                          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                        >
                          Portrait zuschneiden
                        </Button>
                      </Stack>
                      {(!hasHeroPhoto || !hasPortraitPhoto) && (
                        <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.75, display: "block" }}>
                          Zuschneiden ist verfügbar, sobald das jeweilige Foto hochgeladen ist.
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "sections"}
                onChange={handleAccordionChange("sections")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>Abschnitte</Typography>
                    {hiddenSectionKeys.length > 0 && (
                      <Chip
                        size="small"
                        label={`${hiddenSectionKeys.length} ausgeblendet`}
                        sx={{
                          height: 20,
                          borderRadius: 999,
                          fontWeight: 700,
                          fontSize: 11,
                          bgcolor: "rgba(125,156,20,0.12)",
                          color: accentColor,
                        }}
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                    Reihenfolge ändern oder Abschnitte ein-/ausblenden.
                  </Typography>
                  <Stack spacing={0.75}>
                    {(() => {
                      let lastPage = null;
                      return orderedSectionKeys.map((k, idx) => {
                      const hidden = sectionVisibility?.[k] === false;
                      const page = sectionPageMap[k];
                      let pageBreak = null;
                      if (!hidden && page != null) {
                        if (lastPage != null && page > lastPage) {
                          pageBreak = (
                            <Box
                              key={`brk-${k}`}
                              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25, px: 0.5 }}
                              title={`Hier beginnt Seite ${page + 1}`}
                            >
                              <Box sx={{ flex: 1, borderTop: "2px dashed", borderColor: accentColor, opacity: 0.55 }} />
                              <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: accentColor, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                                Seite {page + 1}
                                {/* Pfeil nach unten: die benannte Seite ist die UNTER der Linie */}
                                <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                              </Typography>
                              <Box sx={{ flex: 1, borderTop: "2px dashed", borderColor: accentColor, opacity: 0.55 }} />
                            </Box>
                          );
                        }
                        lastPage = page;
                      }
                      return (
                        <React.Fragment key={k}>
                        {pageBreak}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 2,
                            border: "none",
                            bgcolor: hidden ? "rgba(15,23,42,0.05)" : "rgba(125,156,20,0.08)",
                            opacity: hidden ? 0.6 : 1,
                          }}
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontWeight: 600,
                              fontSize: 13,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textDecoration: hidden ? "line-through" : "none",
                            }}
                          >
                            {sectionLabels[k] || k}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => moveSection(k, -1)}
                            disabled={idx === 0}
                            aria-label="Nach oben"
                            title="Nach oben"
                          >
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveSection(k, 1)}
                            disabled={idx === orderedSectionKeys.length - 1}
                            aria-label="Nach unten"
                            title="Nach unten"
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => toggleSectionVisibility(k)}
                            aria-label={hidden ? "Einblenden" : "Ausblenden"}
                            title={hidden ? "Einblenden" : "Ausblenden"}
                            sx={{ color: hidden ? accentColor : "rgba(15,23,42,0.55)" }}
                          >
                            {hidden ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                        </React.Fragment>
                      );
                      });
                    })()}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "dividers"}
                onChange={handleAccordionChange("dividers")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Typography sx={{ fontWeight: 700 }}>Übergänge</Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Stack spacing={2}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Stil und Abstand aller Trenner auf einmal ändern. Einzelne
                      Trenner lassen sich weiterhin direkt in der Vorschau anpassen.
                    </Typography>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Stil für alle
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {dividerTypeOptions.map((opt) => {
                          const isOn = commonDividerType === opt.id;
                          return (
                            <Button
                              key={opt.id}
                              size="small"
                              variant={isOn ? "contained" : "outlined"}
                              onClick={() => setAllDividers({ type: opt.id })}
                              sx={{
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 700,
                                minWidth: 0,
                                px: 1.25,
                              }}
                              title={opt.label}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <DividerTypeIcon type={opt.id} />
                                {opt.label}
                              </Box>
                            </Button>
                          );
                        })}
                      </Stack>
                      {commonDividerType === null && (
                        <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.75, display: "block" }}>
                          Aktuell uneinheitlich – ein Stil setzt alle gleich.
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Abstand für alle
                      </Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => nudgeAllDividerSpacing(-dividerSpacingStep)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            border: softBorder,
                          }}
                          aria-label="Abstand kleiner"
                          title="Abstand kleiner"
                        >
                          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>−</Typography>
                        </IconButton>
                        <Typography sx={{ fontWeight: 700, minWidth: 44, textAlign: "center" }}>
                          {avgDividerSpacing}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => nudgeAllDividerSpacing(dividerSpacingStep)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            border: softBorder,
                          }}
                          aria-label="Abstand größer"
                          title="Abstand größer"
                        >
                          <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>+</Typography>
                        </IconButton>
                      </Stack>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "nameStyle"}
                onChange={handleAccordionChange("nameStyle")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Typography sx={{ fontWeight: 700 }}>Künstlername</Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Stack spacing={2}>
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel id="name-font-label">Schriftart</InputLabel>
                        <Select
                          labelId="name-font-label"
                          label="Schriftart"
                          value={nameFontId}
                          onChange={(e) => setNameFontId(e.target.value)}
                        >
                          {fontOptions.map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: f.css }}>
                                  {f.label}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: f.css }}>
                                  Aa Bb Cc 123
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Schriftgröße
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {nameSizeOptions.map((opt) => {
                          const active = nameFontSize === opt.value;
                          return (
                            <Button
                              key={opt.id}
                              size="small"
                              variant={active ? "contained" : "outlined"}
                              onClick={() => setNameFontSize(opt.value)}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                            >
                              {opt.label}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Schriftfarbe
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {nameColorOptions.map((opt) => {
                          const active = nameColorKey === opt.key;
                          return (
                            <Button
                              key={opt.id}
                              size="small"
                              variant={active ? "contained" : "outlined"}
                              onClick={() => setNameColorKey(opt.key)}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    bgcolor: resolvePaletteColor(opt.key),
                                    border: "1px solid rgba(0,0,0,0.2)",
                                  }}
                                />
                                {opt.label}
                              </Box>
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "titleStyle"}
                onChange={handleAccordionChange("titleStyle")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Typography sx={{ fontWeight: 700 }}>Überschriften</Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Stack spacing={2}>
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel id="title-font-label">Schriftart</InputLabel>
                        <Select
                          labelId="title-font-label"
                          label="Schriftart"
                          value={titleFontId}
                          onChange={(e) => setTitleFontId(e.target.value)}
                        >
                          {fontOptions.map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: f.css }}>
                                  {f.label}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: f.css }}>
                                  Aa Bb Cc 123
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Schriftgröße
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {titleSizeOptions.map((opt) => {
                          const active = titleFontSize === opt.value;
                          return (
                            <Button
                              key={opt.id}
                              size="small"
                              variant={active ? "contained" : "outlined"}
                              onClick={() => setTitleFontSize(opt.value)}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                            >
                              {opt.label}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                    <Typography variant="caption" sx={{ color: "rgba(15,23,42,0.55)" }}>
                      Die Farbe der Überschriften wird automatisch passend zum
                      Abschnitts-Hintergrund gewählt.
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "bodyStyle"}
                onChange={handleAccordionChange("bodyStyle")}
                disableGutters
                square
                elevation={0}
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                  <Typography sx={{ fontWeight: 700 }}>Textblock</Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Stack spacing={2}>
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputLabel id="font-select-label">Schriftart</InputLabel>
                        <Select
                          labelId="font-select-label"
                          label="Schriftart"
                          value={fontId}
                          onChange={(e) => setFontId(e.target.value)}
                        >
                          {(bodyFontOptions || fontOptions).map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: f.css }}>
                                  {f.label}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: f.css }}>
                                  Aa Bb Cc 123
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Schriftgröße
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {bodySizeOptions.map((opt) => {
                          const active = bodyFontSize === opt.value;
                          const disabled = bodySizeDisabledValues.includes(opt.value);
                          return (
                            <Button
                              key={opt.id}
                              size="small"
                              variant={active ? "contained" : "outlined"}
                              disabled={disabled}
                              onClick={() => setBodyFontSize(opt.value)}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                            >
                              {opt.label}
                            </Button>
                          );
                        })}
                      </Stack>
                      {bodySizeDisabledValues.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(180,83,9,0.95)", mt: 0.9, display: "block", lineHeight: 1.4 }}
                        >
                          Ausgegraute Größen sind gesperrt: Dein Bio-Text würde damit über die
                          Seite hinausragen. Kürze den Text etwas – dann stehen sie wieder zur
                          Verfügung.
                        </Typography>
                      )}
                    </Box>
                    {lineSpacingOptions.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Zeilenabstand
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {lineSpacingOptions.map((opt) => {
                            const active = (lineSpacing || "normal") === opt.id;
                            const disabled = lineSpacingDisabledIds.includes(opt.id);
                            return (
                              <Button
                                key={opt.id}
                                size="small"
                                variant={active ? "contained" : "outlined"}
                                disabled={disabled}
                                onClick={() => setLineSpacing(opt.id)}
                                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                              >
                                {opt.label}
                              </Button>
                            );
                          })}
                        </Stack>
                        {lineSpacingDisabledIds.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(180,83,9,0.95)", mt: 0.9, display: "block", lineHeight: 1.4 }}
                          >
                            Ausgegraute Abstände sind gesperrt: Dein Bio-Text würde damit über
                            die Seite hinausragen. Kürze den Text etwas – dann stehen sie wieder
                            zur Verfügung.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>

            </Stack>

            {(() => {
              // Leerraum-Tipp: Wenn eine Inhaltsseite auffällig viel freie Höhe hat
              // (Sektionen füllen sie nur zum Teil), unter den Einstellungen einen
              // farblich markierten Hinweis mit konkreten Auswegen anzeigen.
              const worst = (sectionGeometry || []).reduce(
                (best, pg, idx) =>
                  (pg?.freeHeight || 0) > (best?.freeHeight || 0)
                    ? { freeHeight: pg.freeHeight, pageNo: idx + 1 }
                    : best,
                null
              );
              if (!worst || worst.freeHeight < 300) return null;
              return (
                <Box
                  sx={{
                    px: 1.75,
                    py: 1.5,
                    borderRadius: 2.5,
                    border: `1px solid ${accentColor}55`,
                    borderLeft: `4px solid ${accentColor}`,
                    background: "linear-gradient(135deg, rgba(125,156,20,0.07), rgba(195,240,76,0.07))",
                    display: "flex",
                    gap: 1.25,
                    alignItems: "flex-start",
                  }}
                >
                  <AutoFixHighIcon sx={{ color: accentColor, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: "rgba(15,23,42,0.85)", lineHeight: 1.55 }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      Tipp: Auf Seite {worst.pageNo} ist noch viel Leerraum.
                    </Box>{" "}
                    Verschiebe unter „Abschnitte" einen kurzen Abschnitt zu einem
                    langen – oder wähle unter „Titel" und „Text" eine größere Schrift
                    bzw. mehr Zeilenabstand. So füllen sich die Seiten gleichmäßiger
                    und können auch zusammenrutschen.
                  </Typography>
                </Box>
              );
            })()}

            {!isPro && (
              <Box
                sx={{
                  position: "relative",
                  px: 2.2,
                  py: 2,
                  // Dezente Rundung (fixe px statt Theme-Multiplikator, der hier 36px ergab)
                  borderRadius: "12px",
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "center",
                  gap: 1.5,
                  background:
                    "linear-gradient(135deg, rgba(125,156,20,0.20), rgba(195,240,76,0.22))",
                  border: "1px solid rgba(125,156,20,0.32)",
                  boxShadow: "0 18px 40px rgba(23,23,15,0.22)",
                  backdropFilter: "blur(12px)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: -80,
                    background:
                      "radial-gradient(420px 220px at 10% 10%, rgba(23,23,15,0.18), transparent 60%), radial-gradient(420px 240px at 90% 10%, rgba(163,216,30,0.22), transparent 60%)",
                    pointerEvents: "none",
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                      Premium
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    Vollen Zugriff zum Generator
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.4,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    PDFs für 2 Jahre | 8 Video-Einheiten | Premium Support
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={onPremiumClick}
                  sx={{
                    borderRadius: 1.5,
                    fontWeight: 800,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    alignSelf: "flex-start",
                    px: 3,
                    color: "#14150c",
                    background: "linear-gradient(135deg, #c3f04c 0%, #a3d81e 100%)",
                    boxShadow: "0 14px 32px rgba(23,23,15,0.35)",
                  }}
                >
                  Vollversion sichern
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
}
